import { Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ActionError, Empty, QueryView } from '../../components/states';
import { Badge, type Tone } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table';
import { useApi } from '../../lib/auth';
import { day, money } from '../../lib/format';
import { useI18n } from '../../lib/i18n';
import { useApiQuery } from '../../lib/query';
import type { SubscriptionInvoiceList } from '../../lib/types';
import { download } from '../policyholder-rights';

// Factures et moyen de paiement de l'abonnement (BA-089) ; retour de la signature du mandat.

const INVOICE_TONES: Record<string, Tone> = { open: 'warning', paid: 'success', void: 'neutral' };

export function BillingCard() {
  const { t, language } = useI18n();
  const api = useApi();
  const client = useQueryClient();
  const query = useApiQuery<SubscriptionInvoiceList>('/v1/subscription/invoices');
  const [error, setError] = useState<Error | null>(null);
  const [pending, setPending] = useState(false);
  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    setPending(true);
    try {
      await action();
      await client.invalidateQueries({ queryKey: ['/v1/subscription/invoices'] });
    } catch (err) {
      setError(err as Error);
    } finally {
      setPending(false);
    }
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.billing.title}</CardTitle>
      </CardHeader>
      <QueryView query={query}>
        {(data) => (
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 text-sm">
              <p>
                {t.billing.method} :{' '}
                <strong>
                  {data.paymentMethod === 'direct_debit'
                    ? t.billing.directDebit
                    : t.billing.transfer}
                </strong>
                {data.mandateStatus === 'pending' ? ` (${t.billing.mandatePending})` : ''}
              </p>
              {data.paymentMethod === 'transfer' ? (
                <p className="text-muted-foreground">{t.billing.transferHint}</p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {data.mandateStatus === 'active' ? (
                  <Button
                    variant="outline"
                    disabled={pending}
                    onClick={() =>
                      void run(() => api.request('DELETE', '/v1/subscription/mandate'))
                    }
                  >
                    {t.billing.stopDirectDebit}
                  </Button>
                ) : (
                  <Button
                    disabled={pending}
                    onClick={() =>
                      void run(async () => {
                        const { url } = await api.request<{ url: string }>(
                          'POST',
                          '/v1/subscription/mandate',
                        );
                        window.location.assign(url);
                      })
                    }
                  >
                    {t.billing.startDirectDebit}
                  </Button>
                )}
              </div>
              <ActionError error={error} />
            </div>
            {data.items.length === 0 ? (
              <Empty>{t.billing.noInvoice}</Empty>
            ) : (
              <Table>
                <THead>
                  <tr>
                    <TH>{t.billing.number}</TH>
                    <TH>{t.billing.period}</TH>
                    <TH className="text-end">{t.billing.total}</TH>
                    <TH>{t.billing.dueOn}</TH>
                    <TH>{t.billing.status}</TH>
                    <TH>
                      <span className="sr-only">{t.billing.download}</span>
                    </TH>
                  </tr>
                </THead>
                <TBody>
                  {data.items.map((inv) => (
                    <TR key={inv.id}>
                      <TD className="whitespace-nowrap">{inv.number}</TD>
                      <TD>{inv.period}</TD>
                      <TD className="text-end tabular-nums">{money(inv.total, language)}</TD>
                      <TD>{day(inv.dueOn, language)}</TD>
                      <TD>
                        <Badge tone={INVOICE_TONES[inv.status] ?? 'neutral'}>
                          {inv.status === 'open' && inv.dueOn < today
                            ? t.billing.overdue
                            : t.billing.statuses[inv.status]}
                        </Badge>
                        {inv.failureReason ? (
                          <p className="mt-1 text-xs text-destructive">{inv.failureReason}</p>
                        ) : null}
                      </TD>
                      <TD className="text-end">
                        {inv.fileId ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              void download(
                                api,
                                `/v1/exports-files/${inv.fileId as string}`,
                                `${inv.number}.pdf`,
                              ).catch((e: Error) => setError(e))
                            }
                          >
                            {t.billing.download}
                          </Button>
                        ) : null}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </CardContent>
        )}
      </QueryView>
    </Card>
  );
}

/** Retour du prestataire après signature : on revient sur l'onglet Abonnement. */
export function SubscriptionReturnPage() {
  return <Navigate to="/parametres" search={{ tab: 'abonnement' }} />;
}

/** Signature simulée du mandat (prestataire factice, recette). */
export function MandateSimulationPage() {
  const { t } = useI18n();
  const api = useApi();
  const navigate = useNavigate();
  const { mandat } = useSearch({ strict: false }) as { mandat?: string };
  const [error, setError] = useState<Error | null>(null);
  const answer = async (outcome: 'signed' | 'refused') => {
    setError(null);
    try {
      await api.request('POST', '/v1/subscription/mandate/simulate', {
        body: { mandateId: mandat, outcome },
      });
      await navigate({ to: '/parametres', search: { tab: 'abonnement' } });
    } catch (err) {
      setError(err as Error);
    }
  };
  return (
    <Card className="mx-auto max-w-lg">
      <CardHeader>
        <CardTitle>{t.billing.simulationTitle}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">{t.billing.simulationIntro}</p>
        <div className="flex gap-2">
          <Button onClick={() => void answer('signed')}>{t.billing.sign}</Button>
          <Button variant="outline" onClick={() => void answer('refused')}>
            {t.billing.refuse}
          </Button>
        </div>
        <ActionError error={error} />
      </CardContent>
    </Card>
  );
}
